import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { colors } from "../../../styles/style";
import goBackIcon from "../../../images/goBackIcon.svg";
import addIcon from "../../../images/addIcon.svg";
import Button from "../../../helpers/components/Button";
import {
  BigHeaderText,
  FlexRowBetweenCenter,
  HeaderText,
  MediumIcon,
  SmallestText,
} from "../../shared/SharedStyles";
import { getFirstWordsByBaby } from "../../../api";
import { useBabyContext } from "../../../context/BabyContext";
import LoadingSpinner from "../../../common/LoadingSpinner";
import {
  birthTimestampToLocalDay,
  formatTimestampToDefaultDate,
} from "../../../common/helpers";
import { NoBabySelectedErrorText } from "../../profile/ProfilePage";
import { FirstWord } from "../../../interface/FirstWordInterface";
import {
  MilestoneButtonWrapper,
  MilestoneContainer,
} from "../common/SharedComponents";
import { FirstWordsGarden } from "./common/FirstWordsGarden";
import { FirstWordsEmptyState } from "./common/FirstWordsEmptyState";
import { FirstWordModal } from "./common/FirstWordModal";
import { FirstWordDetailsModal } from "./common/FirstWordDetailsModal";
import {
  FirstWordScriptText,
  SheetErrorText,
  SoundedLikeText,
  SparkleIcon,
  firstWordErrorMessages,
  formatAgeOldAt,
  formatSoundedLike,
} from "./common/FirstWordsShared";
import { useCanEdit } from "../../../common/useCanEdit";

interface WordEditorState {
  word?: FirstWord;
}

const compareNewestFirst = (a: FirstWord, b: FirstWord) =>
  b.said_on - a.said_on || b.created_at - a.created_at;

const formatWordCount = (count: number) =>
  `${count} ${count === 1 ? "word" : "words"} so far`;

export const FirstWordsPage = () => {
  const navigate = useNavigate();
  const canEdit = useCanEdit("first_words");
  const { selectedBabyId, selectedBaby } = useBabyContext();
  const [words, setWords] = useState<FirstWord[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [detailsWordId, setDetailsWordId] = useState<string | null>(null);
  const [editor, setEditor] = useState<WordEditorState | null>(null);
  const latestRequestId = useRef(0);

  const dateOfBirth: number | undefined =
    typeof selectedBaby?.date_of_birth === "number"
      ? birthTimestampToLocalDay(selectedBaby.date_of_birth)
      : undefined;

  const sortedWords = useMemo(
    () => (words ? [...words].sort(compareNewestFirst) : []),
    [words]
  );
  const earliestWord = sortedWords[sortedWords.length - 1];
  const detailsWord = sortedWords.find((word) => word.id === detailsWordId);

  const fetchWords = useCallback(async () => {
    if (!selectedBabyId) return;
    latestRequestId.current += 1;
    const requestId = latestRequestId.current;
    try {
      const { data } = await getFirstWordsByBaby(selectedBabyId);
      if (requestId !== latestRequestId.current) return;
      setWords(Array.isArray(data) ? data : []);
      setLoadFailed(false);
    } catch (error) {
      if (requestId !== latestRequestId.current) return;
      console.error("Error fetching first words", error);
      setLoadFailed(true);
    }
  }, [selectedBabyId]);

  useEffect(() => {
    setWords(null);
    setLoadFailed(false);
    setDetailsWordId(null);
    setEditor(null);
    fetchWords();
  }, [fetchWords]);

  const goBackHandler = () => {
    navigate(-1);
  };

  const openAddWord = () => {
    if (canEdit) setEditor({});
  };

  const openEditWord = (word: FirstWord) => {
    setDetailsWordId(null);
    setEditor({ word });
  };

  const closeSheetsAndRefetch = () => {
    setEditor(null);
    setDetailsWordId(null);
    fetchWords();
  };

  const isEmpty = words !== null && sortedWords.length === 0;

  return (
    <React.Fragment>
      <MilestoneContainer>
        <HeaderContainer>
          <MediumIcon src={goBackIcon} onClick={goBackHandler} />
          <BigHeaderText>First Words</BigHeaderText>
          <div></div>
        </HeaderContainer>
        {!selectedBabyId && <NoBabySelectedErrorText />}
        {/* Also shown when a refetch after a save fails, so a stale list
            never passes silently for the saved one. */}
        {loadFailed && (
          <SheetErrorText role="alert">
            {firstWordErrorMessages.generic}
          </SheetErrorText>
        )}
        {selectedBabyId && words === null && !loadFailed && <LoadingSpinner />}
        {isEmpty && (
          <FirstWordsEmptyState
            canAdd={canEdit}
            onAdd={openAddWord}
            onGoBack={goBackHandler}
          />
        )}
        {earliestWord && (
          <React.Fragment>
            <HeroCard>
              <HeroQuoteMark aria-hidden="true">“</HeroQuoteMark>
              <HeroEyebrow>
                <SparkleIcon />
                <HeroEyebrowText>My very first word</HeroEyebrowText>
              </HeroEyebrow>
              <HeroWord>{earliestWord.word}</HeroWord>
              {earliestWord.sounded_like && (
                <HeroSoundedLike>
                  {formatSoundedLike(earliestWord.sounded_like)}
                </HeroSoundedLike>
              )}
              <HeroMeta>
                <DatePill>
                  {formatTimestampToDefaultDate(earliestWord.said_on)}
                </DatePill>
                {dateOfBirth !== undefined && (
                  <HeroAge>
                    {formatAgeOldAt(dateOfBirth, earliestWord.said_on)}
                  </HeroAge>
                )}
              </HeroMeta>
            </HeroCard>
            <Stats>
              <StatsText>
                <HeaderText>{formatWordCount(sortedWords.length)}</HeaderText>
                <StatsCaption>Newest first</StatsCaption>
              </StatsText>
              <AddWordButton
                type="button"
                onClick={openAddWord}
                disabled={!canEdit}
              >
                <AddWordIcon src={addIcon} alt="" />
                <span>Add word</span>
              </AddWordButton>
            </Stats>
            <FirstWordsGarden
              words={sortedWords}
              earliestWordId={earliestWord.id}
              dateOfBirth={dateOfBirth}
              onWordClick={(word) => setDetailsWordId(word.id)}
            />
          </React.Fragment>
        )}
        {!isEmpty && (
          <GoBackWrapper>
            <Button $button_type="colored" onClick={goBackHandler}>
              Go Back
            </Button>
          </GoBackWrapper>
        )}
      </MilestoneContainer>
      {editor && (
        <FirstWordModal
          word={editor.word}
          dateOfBirth={dateOfBirth}
          onClose={() => setEditor(null)}
          onSaved={closeSheetsAndRefetch}
          onWordGone={closeSheetsAndRefetch}
        />
      )}
      {detailsWord && (
        <FirstWordDetailsModal
          key={detailsWord.id}
          word={detailsWord}
          dateOfBirth={dateOfBirth}
          onClose={() => setDetailsWordId(null)}
          onEdit={() => openEditWord(detailsWord)}
          onRemoved={closeSheetsAndRefetch}
        />
      )}
    </React.Fragment>
  );
};

const HeaderContainer = styled.div`
  ${FlexRowBetweenCenter}
  padding: 16px;
  width: 100%;
`;

const HeroCard = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 4px 24px 0;
  padding: 20px 24px 24px;
  background-color: ${colors.whiteBackgroundColor};
  border-radius: 32px 32px 32px 8px;
`;

const HeroQuoteMark = styled.span`
  position: absolute;
  right: 18px;
  top: -22px;
  font-family: "Clicker Script";
  font-size: 150px;
  line-height: 150px;
  color: ${colors.quoteMarkBeige};
  pointer-events: none;
`;

const HeroEyebrow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeroEyebrowText = styled.span`
  ${SmallestText}
  color: ${colors.softBrown};
`;

const HeroWord = styled(FirstWordScriptText)`
  position: relative;
  text-align: left;
`;

const HeroSoundedLike = styled(SoundedLikeText)`
  position: relative;
`;

const HeroMeta = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
`;

const DatePill = styled.span`
  ${SmallestText}
  padding: 3px 6px;
  border-radius: 8px;
  background-color: ${colors.darkBrown};
  color: ${colors.white};
`;

const HeroAge = styled.span`
  ${SmallestText}
  color: ${colors.warmthBrown};
`;

const Stats = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 28px 24px 0;
`;

const StatsText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatsCaption = styled.span`
  ${SmallestText}
  color: ${colors.softBrown};
`;

const AddWordButton = styled.button`
  ${SmallestText}
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  height: 44px;
  padding: 0 16px;
  border: 1px solid ${colors.darkBrown};
  border-radius: 31px;
  background-color: transparent;
  color: ${colors.softBrown};
  font-family: inherit;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const AddWordIcon = styled.img`
  width: 14px;
  height: 14px;
`;

const GoBackWrapper = styled(MilestoneButtonWrapper)`
  padding-top: 32px;
`;
